import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { IAccount } from '@services/accounts.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { Rule, RuleDefinition, RulesDataService } from '@services/rules-data.service';
import { distinctUntilChanged, filter, finalize, of, Subject, takeUntil } from 'rxjs';
import { CreateRuleComponent, EditRuleDialogData } from 'src/app/dialogs/create-rule/create-rule.component';
import { convertEpochToDateString } from '@functions/functions'
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { EventsDataService, RuleEvent } from '@services/events-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';

@Component({
	selector: 'app-dashboard-rule-events',
	templateUrl: './dashboard-rule-events.component.html',
	styleUrls: ['./dashboard-rule-events.component.scss']
})
export class DashboardRuleEventsComponent implements OnChanges, OnInit, OnDestroy {

	/** ruleEvents */
	public ruleEvents: RuleEvent[] = [];

	/** rules */
	public rules: any[] = [];

	/** fields */
	public fields: Object = { text: 'RuleText', id: 'RuleEventID' };

	/** SelectedEvent */
	public SelectedEvent: RuleEvent | undefined;

	/** EventRule */
	public EventRule: Rule;

	/** EventAsset */
	public EventAsset: LevelAsset | undefined;

	/** expanded */
	@Output() expanded = new EventEmitter<boolean>();

	/** accountID */
	@Input() accountID: string;

	/** selectedAccount */
	@Input() public selectedAccount: IAccount;

	/** OrganisationID */
	@Input() public organisationUID: string;

	resizeObserver!: ResizeObserver;

	public isMobileView: boolean;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	public currentUser: WebAppUser

	public hierarchy: OrganisationsHierarchy[];

	public flattenedAssetIDs: number[] = [];

	/**
	 * Default Constructor
	 * @param rulesDataService rulesDataService
	 * @param eventsDataService eventsDataService
	 * @param assetsDataService assetsDataService
	 * @param authenticationService authenticationService
	 * @param dialog dialog
	 * @param router router
	 * @param globalFunctions globalFunctions
	 * @param siteSubscriptions siteSubscriptions
	 */
	constructor(
		public rulesDataService: RulesDataService,
		public eventsDataService: EventsDataService,
		public assetsDataService: AssetsDataService,
		private authenticationService: AuthenticationService,
		public dialog: MatDialog,
		private router: Router,
		private globalFunctions: GlobalFunctions,
		private siteSubscriptions: siteSubscriptions
	) {
		this.subscribeCurrentUser();
		this.subscribeHierarchy();
	}

	subscribeHierarchy() {
		this.siteSubscriptions.hierarchicalData$.pipe(
			takeUntil(this.finalise), distinctUntilChanged(),
			filter((hierarchy) => !!hierarchy)
		).subscribe({
			next: (hierarchy) => {
				this.hierarchy = JSON.parse(hierarchy);
				let list: LevelAsset[] = this.flattenAssets(this.hierarchy);
				list.forEach(asset => {
					this.flattenedAssetIDs.push(asset.AssetID);
				})
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.authenticationService.currentUser$", false) },
		})
	}

	/**
	 * subscribeCurrentUser
	 */
	subscribeCurrentUser() {
		this.authenticationService.currentUser$.pipe(
			takeUntil(this.finalise), distinctUntilChanged(),
			filter((currentUser) => !!currentUser)
		).subscribe({
			next: (currentUser) => {
				this.currentUser = <WebAppUser>currentUser;
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.authenticationService.currentUser$", false) },
		})
	}

	/** setupScreenWidthObserver */
	setupScreenWidthObserver() {
		// Set initial value based on the current window width
		this.isMobileView = window.innerWidth < 1050;

		// Add a resize event listener to the window
		window.addEventListener('resize', this.updateIsMobileView.bind(this));
	}

	/** updateIsMobileView */
	updateIsMobileView() {
		this.isMobileView = window.innerWidth < 1050;
	}

	/**
	 * ngOnInit
	 */
	ngOnInit() {
		this.setupScreenWidthObserver();
	}

	/**
	 * ngOnChanges
	 * @param changes
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (changes) { this.getRuleEvents() }
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			window.removeEventListener('resize', this.updateIsMobileView.bind(this));
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * getRules
	 */
	getRuleEvents() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.eventsDataService.getRuleEvents$(this.accountID, this.organisationUID, 0).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((ruleEvents) => !!ruleEvents),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (ruleEvents: any) => {
				this.recurFilterRuleEvents(ruleEvents);
				// this.getRules()
			}, error: (error) => {
				// Handle the 404 error: e.g., reset state or call a method
				if (error.status === 404) {
					this.ruleEvents = [];
					this.deselectEvent();
				}
			}
		});
	}

	/** Waiting for the hierarchy to be populated before trying to use it to filter the rule events */
	recurFilterRuleEvents(ruleEvents: RuleEvent[]) {
		if (this.hierarchy) {
			this.ruleEvents = ruleEvents.filter((x: RuleEvent) => this.flattenedAssetIDs.includes(x.AssetID));
		} else {
			setTimeout(() => {
				this.recurFilterRuleEvents(ruleEvents);
			}, 10);
		}
	}

	/** getRules */
	getRules() {
		for (let i = 0; i < this.ruleEvents.length; i++) {
			// Create a local Subject to control the subscription lifecycle
			const fetchRuleDataSubject = new Subject<void>();
			this.rulesDataService.getRules$(this.accountID, this.organisationUID, 0, this.ruleEvents[i].RuleID).pipe(
				takeUntil(fetchRuleDataSubject), distinctUntilChanged(),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchRuleDataSubject.next();
					fetchRuleDataSubject.complete();
				})
			).subscribe({
				next: async (rules: any) => {
					this.rules.push(rules[0]);
				},
			});
		}
	}

	/**
	 * goToEvent
	 * @param selectedEvent
	 */
	goToEvent(selectedEvent: RuleEvent) {
		this.router.navigate([`./${this.accountID}/organisation/${this.organisationUID}/event/${selectedEvent.RuleEventID}`], {
			queryParams: { eventType: '2' }
		});
	}

	/**
	 * RuleEventSelected
	 * @param data
	 */
	RuleEventSelected(data: RuleEvent) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.SelectedEvent = data;
		this.rulesDataService.getRules$(this.accountID, this.organisationUID, 0, this.SelectedEvent.RuleID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: async (rules: any) => {
				this.EventRule = rules[0];
				this.getEventAsset()
			}, error: (error) => {
				if (error.status == 404) {
					this.ruleEvents = []
					this.deselectEvent()
				}
			}
		});

		setTimeout(() => {
			this.expanded.emit(true)
		}, 100);
	}

	/**
	 * popupRuleEvent
	 */
	popupRuleEvent(data: RuleEvent) {

	}

	getEventAsset() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.getOrganisationAssets$(this.accountID, this.organisationUID, this.EventRule.AssetID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (asset: any) => {
				this.EventAsset = asset;
			},
			error: (error) => {
				if (error.status == 404) {
					this.EventAsset = undefined
				} else { this.globalFunctions.HandleError(error, "Error: AssetsDataService.getOrganisationAssets$", false); }
			},
		});
	}

	/**
	 * deselectEvent
	 */
	deselectEvent() {
		this.SelectedEvent = undefined
		this.expanded.emit(false)
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch)
	}

	/**
	 * splitValues
	 * @param text
	 * @returns
	 */
	splitValues(text: string): string[] { return text.split(';') }

	/** extractOperator */
	extractOperator(text: string): string {
		if (text.includes("OR")) {
			return "OR"
		} else if (text.includes("AND")) {
			return "AND"
		} else {
			return ""
		}
	}

	/**
	 * Converts int of total minutes to [HH,MM] string array
	 * @param totalMinutes
	 * @returns
	 */
	convertMinutesToHHMM(totalMinutes: number): string[] {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const hoursString = hours.toString().padStart(2, '0');
		const minutesString = minutes.toString().padStart(2, '0');
		return [hoursString, minutesString]
	}

	/**
	 * checkIfRuleTriggered
	 * @param ruleID
	 * @returns
	 */
	checkIfRuleTriggered(ruleID: number): boolean {
		let rule: Rule = this.rules.find((x: Rule) => x.RuleID == ruleID);
		if (rule) {
			if (rule.Active == true) {
				return true
			}
		}
		return false;
	}

	/**
	 * getAssetName
	 * @param ruleID
	 * @returns
	 */
	getAssetName(ruleID: number): string {
		let rule: Rule = this.rules.find((x: Rule) => x.RuleID == ruleID);
		if (rule) {
			return rule.AssetName ?? ""
		}
		return "";
	}

	/**
	 * OpenRule
	 * @param rule
	 */
	OpenRule(rule: Rule) {
		var _edit = { ...rule };

		let time: string = this.convertMinutesToHHMM(_edit.TriggerTime)[0] + ":" + this.convertMinutesToHHMM(_edit.TriggerTime)[1]

		let dialogData = <EditRuleDialogData>{
			assetItem: this.EventAsset,
			ruleItem: _edit,
			triggerTime: time,
			comparison: _edit.RuleText.match(/AND|OR/)?.[0] || "",
			readonly: !this.selectedAccount.Admin
		}

		const dialogRef = this.dialog.open(CreateRuleComponent, {
			data: dialogData
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			if (result.ruleEnabled == null) {
				rule.RuleEnabled = false;
			} else {
				rule.RuleEnabled = result.ruleEnabled
			}
			rule.RuleDescription = result.ruleDescription
			rule.RuleMessage = result.ruleMessage
			rule.TriggerTime = this.timeToMinutes(result.ruleTime)

			//split the rule into the rule-definition composition
			if (rule.RuleDef) {
				const data1 = {
					RuleDefID: rule.RuleDef[0].RuleDefID,
					RuleID: rule.RuleID,
					Condition: 1,
					DatapointID: result.dataPoint1,
					Limit: result.value1,
					Operator: result.condition1
				}
				let definition1: RuleDefinition = data1;

				let definition2 = undefined;

				/** if we already had another definition */
				if (result.dataPoint2 && rule.RuleDef[1]) {
					const data2 = {
						RuleDefID: rule.RuleDef[1].RuleDefID,
						RuleID: rule.RuleID,
						Condition: 2,
						DatapointID: result.dataPoint2,
						Limit: result.value2,
						Operator: result.condition2
					}
					definition2 = data2;
					/** if a new definition is being added */
				} else if (result.dataPoint2 && !rule.RuleDef[1]) {
					const data2 = {
						RuleID: rule.RuleID,
						Condition: 2,
						DatapointID: result.dataPoint2,
						Limit: result.value2,
						Operator: result.condition2
					}
					definition2 = data2;
				}

				if (definition2) {
					rule.RuleText = "[1] " + result.comparison + " [2]";
					rule.RuleDef = [definition1, definition2];

				} else {
					rule.RuleText = "[1]"
					rule.RuleDef = [definition1]
				}
			}
			this.patchRule(rule)
		});
	}

	/**
	 * Converts the HH:MM format required for the trigger time to an int of
	 * total minutes
	 * @param timeStr
	 * @returns
	 */
	timeToMinutes(timeStr: string): number {
		const [hours, minutes] = timeStr.split(':').map(Number);
		const totalMinutes = hours * 60 + minutes;
		return totalMinutes;
	}

	/**
	 * patchRule
	 * @param rule
	 */
	patchRule(rule: Rule) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.rulesDataService.patchRule$(this.accountID,
			this.organisationUID, rule.AssetID, rule, this.currentUser.UserID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((sqlResult) => !!sqlResult),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (sqlResult: any) => {
					// console.log(sqlResult)
					this.globalFunctions.DisplaySnackBar({
						message: "Rule Updated",
						type: SnackType.Info,
						duration: 1000
					});
					// this.GetRules(this.selectedAsset)
				},
				error: (error) => {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "Error: this.rulesDataService.patchRule$", false);
					}
				},
			});
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}
}
